import React from 'react'
import PropTypes from 'prop-types'
import flatpickr from 'flatpickr'
import moment from 'moment'
import { css } from 'emotion'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import { flatpickrStyle, defaultStyle } from './style'
import { Input } from '@maxmilhas/hangar-components-atomic/public'

class DatePicker extends React.PureComponent {
  static displayName = 'DatePicker'

  state = {
    isOpen: false
  }

  constructor(props) {
    super(props)

    this.input = React.createRef()

    this.handleCloseOnTab = this.handleCloseOnTab.bind(this)
    this.handleChangeInput = this.handleChangeInput.bind(this)
  }

  componentDidMount() {
    const { minDate, maxDate, defaultDate, onOpen, onClose, onChange } = this.props

    const { inputElement } = this.input.current

    this.instance = flatpickr(inputElement, {
      defaultDate,
      minDate,
      maxDate,
      appendTo: inputElement.parentElement,
      locale: Portuguese,
      monthSelectorType: 'static',
      dateFormat: 'd/m/Y',
      ariaDateFormat: 'F, j Y',
      disableMobile: true,
      allowInput: true,
      static: true,
      onChange: (selectedDates) => {
        onChange && onChange(selectedDates[0])
      },
      onOpen: (selectedDates, dateStr, instance) => {
        document.addEventListener('keydown', this.handleCloseOnTab)
        let min = instance.config.minDate || moment.utc().startOf('day').toDate()
        if (selectedDates.length > 0) {
          min = selectedDates[0]
        }
        instance.jumpToDate(min, true)
        onOpen && onOpen(selectedDates.length > 0 ? selectedDates[0] : null)
        this.setState({ isOpen: true })
      },
      onClose: (selectedDates, dateStr, instance) => {
        document.removeEventListener('keydown', this.handleCloseOnTab)
        if (instance.input.value.length === 0 && instance.latestSelectedDateObj) {
          instance.setDate(instance.latestSelectedDateObj, true)
        }
        onClose && onClose(selectedDates.length > 0 ? selectedDates[0] : null)
        this.setState({ isOpen: false })
      }
    })
  }

  componentDidUpdate(prevProps) {
    if (this.props.defaultDate !== null && (prevProps.defaultDate === null || !moment(this.props.defaultDate).startOf('day').isSame(moment(prevProps.defaultDate).startOf('day')))) {
      this.instance.setDate(this.props.defaultDate, true)
    }
    if (this.props.minDate !== null && (prevProps.minDate === null || !moment(this.props.minDate).startOf('day').isSame(moment(prevProps.minDate).startOf('day')))) {
      this.instance.set('minDate', this.props.minDate)
    }
    if (this.props.maxDate !== null && (prevProps.maxDate === null || !moment(this.props.maxDate).startOf('day').isSame(moment(prevProps.maxDate).startOf('day')))) {
      this.instance.set('maxDate', this.props.maxDate)
    }
  }

  handleCloseOnTab(event) {
    if (event.keyCode === 9) {
      this.instance.close()
    }
  }

  handleChangeInput(event) {
    const currentValue = event.target.value.replace(/[^\d/]/g, '')
    if (currentValue.length === 10) {
      if (moment(currentValue, 'DD/MM/YYYY').isValid()) {
        this.instance.setDate(currentValue, true)
      } else {
        this.instance.setDate(this.instance.latestSelectedDateObj, true)
      }
    }
  }

  render() {
    const {
      id,
      name,
      label,
      placeholder,
      icon,
      shadow,
      hasError,
      defaultDate,
      minDate,
      maxDate,
      onOpen,
      onClose,
      onChange,
      ...rest
    } = this.props

    const { isOpen } = this.state

    return (
      <div className={css`${flatpickrStyle}; ${defaultStyle(isOpen, shadow, hasError)}`}>
        <Input
          ref={this.input}
          id={id}
          name={name}
          label={label}
          placeholder={placeholder}
          icon={icon}
          onChange={this.handleChangeInput}
          mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
          shadow={shadow}
          hasError={hasError}
          {...rest}
        />
      </div>
    )
  }
}

DatePicker.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.string,
  shadow: PropTypes.bool,
  hasError: PropTypes.bool,
  defaultDate: PropTypes.object,
  minDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  maxDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onChange: PropTypes.func
}

DatePicker.defaultProps = {
  name: null,
  label: null,
  placeholder: null,
  icon: null,
  shadow: false,
  hasError: false,
  defaultDate: null,
  minDate: null,
  maxDate: null,
  onOpen: null,
  onClose: null,
  onChange: null
}

export default DatePicker
